/**
 * Central registry of immutable React Query key factories. Exact tuple segment
 * order and every supplied identifier define cache identity, so reads,
 * invalidations, and mutations must use the same factory rather than ad hoc keys.
 * Organization-owned data, including billing wallet and SetupIntent state,
 * includes the organization ID to prevent reuse across organization switches.
 */
export const queryKeys = {
  // User data
  user: {
    me: () => ["user"] as const,
  },

  // Organizations
  organizations: {
    all: () => ["organizations"] as const,
    detail: (organizationId: string) =>
      ["organizations", organizationId] as const,
  },

  // API Keys
  apiKeys: {
    all: (organizationId: string) => ["apiKeys", organizationId] as const,
    detail: (organizationId: string, apiKeyId: string) =>
      ["apiKeys", organizationId, "detail", apiKeyId] as const,
  },

  // Webhooks
  webhooks: {
    all: (organizationId: string) => ["webhooks", organizationId] as const,
    detail: (organizationId: string, webhookId: string) =>
      ["webhooks", organizationId, "detail", webhookId] as const,
  },

  // Members
  members: {
    all: (organizationId: string) => ["members", organizationId] as const,
    detail: (organizationId: string, memberId: string) =>
      ["members", organizationId, "detail", memberId] as const,
  },

  // Invitations
  invitations: {
    all: (organizationId: string) => ["invitations", organizationId] as const,
    detail: (organizationId: string, invitationId: string) =>
      ["invitations", organizationId, "detail", invitationId] as const,
  },

  billing: {
    /** Tracks Checkout requests across organization view unmounts. */
    checkout: (orgId: string) => ["billing", orgId, "checkout"] as const,
    summary: (orgId: string) => ["billing", orgId, "summary"] as const,
    options: (orgId: string) => ["billing", orgId, "options"] as const,
    purchase: (orgId: string, purchaseId: string) =>
      ["billing", orgId, "purchase", purchaseId] as const,
    purchases: (orgId: string) => ["billing", orgId, "purchases"] as const,
    invoices: (orgId: string) => ["billing", orgId, "invoices"] as const,
    /** One stable signup-config cache entry for the current authenticated session. */
    signupConfig: () => ["billing", "signup-config"] as const,
    rateCard: () => ["billing", "rate-card"] as const,
    wallet: (organizationId: string) =>
      ["billing", organizationId, "wallet"] as const,
    setupIntent: (organizationId: string) =>
      ["billing", organizationId, "setup-intent"] as const,
  },

  // Analytics
  analytics: {
    /** In-memory dashboard selections, isolated by organization. */
    view: (orgId: string) => ["analytics", orgId, "view"] as const,
    /** Invalidates only this tenant's history pages when the user explicitly refreshes. */
    histories: (orgId: string) => ["analytics", orgId, "history"] as const,
    /** Independent cursor history and ID lookups, isolated by tenant and filter. */
    history: (orgId: string, params: object) =>
      ["analytics", orgId, "history", params] as const,
    /** One report cache entry per tenant and complete filter selection. */
    report: (orgId: string, params: object) =>
      ["analytics", orgId, "report", params] as const,
  },
};

// Export types directly from the literal return types
export type UserQueryKey = ReturnType<typeof queryKeys.user.me>;
export type OrganizationsQueryKey = ReturnType<
  typeof queryKeys.organizations.all
>;
export type ApiKeysQueryKey = ReturnType<typeof queryKeys.apiKeys.all>;
export type WebhooksQueryKey = ReturnType<typeof queryKeys.webhooks.all>;
export type MembersQueryKey = ReturnType<typeof queryKeys.members.all>;
export type InvitationsQueryKey = ReturnType<typeof queryKeys.invitations.all>;
